import { computed, inject } from '@angular/core';
import {
  signalStore,
  withState,
  withComputed,
  withMethods,
  patchState,
  withHooks,
} from '@ngrx/signals';
import { IUserBasic } from '@interfaces/IUserBasic';
import { AuthLogicService } from '@auth/auth';
import {
  AddressDto,
  DisplayAddressDto,
  ProfileInfoResponse,
  ProfileInfoDto,
  ProfileInfoDtoSchema,
  UserEducationDto,
  UserEmploymentDto,
} from '@gen/src/protos/users/users_pb';
import { UserState } from '@shared/interfaces/IUserState';
import { AuthStateService } from '@core/services/auth/auth-state';
import { create } from '@bufbuild/protobuf';
import { IUserIdentifiers } from '@shared/interfaces/IUserIdentifiers';
import { IUserVerifiedIds } from '@shared/interfaces/IUserVerfiedIds';
import { ValidateResponse } from '@gen/src/protos/auth/v1/auth_pb';

const initialState: UserState = {
  token: null,
  refreshToken: null,
  lastToken: null,
  user: null,
  status: 'idle',
  verifiedIds: null,
  error: null,
};

let loadMePromise: Promise<void> | null = null;

export const UserStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withComputed((s) => ({
    isLoggedIn: computed(() => !!s.token()),
    hasVerifiedIds: computed(() => !!s.verifiedIds()),
    avatarLetter: computed(() => (s.user()?.personal?.name?.[0] ?? '').toUpperCase()),
  })),

  withMethods(
    (s, authService = inject(AuthLogicService), authState = inject(AuthStateService)) => ({
      setToken(token: string) {
        authState.setToken(token);
        patchState(s, {
          token,
          lastToken: token,
          user: null,
          status: 'idle',
          error: null,
        });
      },

      setRefreshToken(refreshToken: string) {
        authState.setRefreshToken(refreshToken);
        patchState(s, { refreshToken, error: null });
      },

      clearAuth() {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('asso-h-c');
        patchState(s, { token: null, refreshToken: null, user: null, status: 'idle', error: null });
      },

      setUser(user: IUserBasic) {
        patchState(s, { user, error: null });
      },

      setProfileInfo(personal: ProfileInfoDto | null) {
        const u = s.user();
        if (!u) return;

        patchState(s, { user: { ...u, personal } });
      },

      setUserAddresses(addresses: AddressDto[] | null) {
        const u = s.user();
        if (!u) return;
        patchState(s, { user: { ...u, addresses } });
      },

      setUserEducations(educations: UserEducationDto[] | null) {
        const u = s.user();
        if (!u) return;

        patchState(s, { user: { ...u, educations } });
      },

      setUserEmployments(employments: UserEmploymentDto[] | null) {
        const u = s.user();
        if (!u) return;

        patchState(s, { user: { ...u, employments } });
      },

      setVerifiedIds(verifiedIds: IUserVerifiedIds | null) {
        const u = s.user();
        if (!u) return;

        patchState(s, { verifiedIds, error: null });
      },

      applyPersonalInfoPatch(patch: Partial<ProfileInfoDto>) {
        const user = s.user();
        if (!user) return;
        patchState(s, {
          user: {
            ...user,
            personal: {
              ...(user.personal ?? ({} as ProfileInfoDto)),
              ...patch,
            },
          },
        });
      },

      applyAddressPatch(patch: Partial<AddressDto>) {
        const user = s.user();
        if (!user || !user.addresses) return;
        const addresses = user.addresses.map((adr) =>
          adr.type === patch.type ? { ...adr, ...patch } : adr,
        );
        patchState(s, {
          user: {
            ...user,
            addresses,
          },
        });
      },

      applyIdentifiersPatch(patch: Partial<IUserIdentifiers>) {
        const user = s.user();
        if (!user) return;
        patchState(s, {
          user: {
            ...user,
            ...patch,
          },
        });
      },

      applyEducationPatch(patch: Partial<UserEducationDto>) {
        const user = s.user();
        if (!user || !user.educations) return;
        const educations = user.educations.map((edu) =>
          edu.Id === patch.Id ? { ...edu, ...patch } : edu,
        );
        patchState(s, {
          user: {
            ...user,
            educations,
          },
        });
      },

      applyEmploymentPatch(patch: Partial<UserEmploymentDto>) {
        const user = s.user();
        if (!user || !user.employments) return;
        const employments = user.employments.map((emp) =>
          emp.id === patch.id ? { ...emp, ...patch } : emp,
        );
        patchState(s, {
          user: {
            ...user,
            employments,
          },
        });
      },

      applyVerifiedIdsPatch(patch: Partial<IUserVerifiedIds>) {
        const verifiedIds = s.verifiedIds();
        if (!verifiedIds) return;
        patchState(s, {
          verifiedIds: {
            ...verifiedIds,
            ...patch,
          },
        });
      },

      async ensureMe() {
        if (s.status() === 'loaded') return;
        if (s.status() === 'loading' && loadMePromise) return loadMePromise;

        loadMePromise = (async () => {
          try {
            patchState(s, { status: 'loading', error: null });

            const response = await authService.getProfileInfo();
            if (!response || !response.code) {
              patchState(s, { status: 'error', error: 'Respuesta inválida' });
              return;
            }

            const username = authState.getJwtClaim<string>('username');

            const [identifiers, addressesResponse] = await Promise.all([
              authService.getUserIdentifiers(),
              authService.getAddresses(),
            ]);

            const personal =
              response.code !== 'AUTH-041' && response.profileInfo
                ? create(ProfileInfoDtoSchema, {
                    name: response.profileInfo.name ?? '',
                    lastName: response.profileInfo.lastName ?? '',
                    secondLastName: response.profileInfo.secondLastName ?? '',
                    urlProfileImage: response.profileInfo.urlProfileImage,
                    birthdate: response.profileInfo.birthdate?.toString() ?? '',
                    genderId: response.profileInfo.genderId,
                  })
                : null;

            const user: IUserBasic = {
              username: username ?? undefined,
              email: identifiers.email ?? undefined,
              phoneNumber: identifiers.phoneNumber ?? undefined,
              personal,
              educations: [],
              employments: [],
              addresses: addressesResponse.addresses.map((x) => ({ ...x })),
              displayAddresses: addressesResponse.displayAddresses.map((x) => ({ ...x })),
            };

            patchState(s, { user, status: 'loaded', error: null });
          } catch (e: Error | unknown) {
            const errorMessage = e instanceof Error ? e.message : 'Error cargando usuario';
            patchState(s, { status: 'error', error: errorMessage });
          } finally {
            loadMePromise = null;
          }
        })();

        return loadMePromise;
      },

      async ensureBasic() {
        if (s.status() === 'loaded') return;

        patchState(s, { status: 'loading' });

        const identifiers = await authService.getUserIdentifiers();
        const response = await authService.getProfileInfo();
        if (!response || !response.code) {
          patchState(s, { status: 'error', error: 'Respuesta inválida' });
          return;
        }
        const personal =
          response.code !== 'AUTH-041' && response.profileInfo
            ? create(ProfileInfoDtoSchema, {
                name: response.profileInfo.name ?? '',
                lastName: response.profileInfo.lastName ?? '',
                secondLastName: response.profileInfo.secondLastName ?? '',
                urlProfileImage: response.profileInfo.urlProfileImage,
                birthdate: response.profileInfo.birthdate?.toString() ?? '',
                genderId: response.profileInfo.genderId,
              })
            : null;

        patchState(s, {
          status: 'loaded',
          user: {
            username: authState.getJwtClaim<string>('username'),
            email: identifiers.email,
            phoneNumber: identifiers.phoneNumber,
            personal,
          } as IUserBasic,
        });
      },

      async loadMe() {
        try {
          patchState(s, { status: 'loading', error: null });

          const response: ProfileInfoResponse | undefined = await authService.getProfileInfo();
          if (!response || !response.code) {
            patchState(s, { status: 'error', error: 'Respuesta inválida' });
            return;
          }
          const username = authState.getJwtClaim<string>('username');
          const identifiers = await authService.getUserIdentifiers();
          const addressesResponse = await authService.getAddresses();
          const personalInfo: ProfileInfoDto = create(ProfileInfoDtoSchema, {});
          if (response.code !== 'AUTH-041') {
            personalInfo.name = response?.profileInfo?.name ?? '';
            personalInfo.lastName = response?.profileInfo?.lastName ?? '';
            personalInfo.secondLastName = response?.profileInfo?.secondLastName ?? undefined;
            personalInfo.urlProfileImage = response?.profileInfo?.urlProfileImage;
            personalInfo.birthdate = response.profileInfo?.birthdate?.toString() ?? '';
            personalInfo.genderId = response.profileInfo?.genderId;
          }
          const addresses: AddressDto[] = addressesResponse.addresses.map((x: AddressDto) => ({
            ...x,
          }));
          const displayAddresses: DisplayAddressDto[] = addressesResponse.displayAddresses.map(
            (x: DisplayAddressDto) => ({ ...x }),
          );
          const user: IUserBasic = {
            username: username ?? undefined,
            email: identifiers.email,
            phoneNumber: identifiers.phoneNumber,
            personal: personalInfo,
            addresses,
            displayAddresses,
            educations: [],
            employments: [],
          };
          this.setUser(user);
          // const me = await Promise.resolve(null as unknown as IUserBasic);

          patchState(s, { user: user, status: 'loaded' });
        } catch (e: Error | unknown) {
          const errorMessage = e instanceof Error ? e.message : 'Error cargando usuario';
          patchState(s, { status: 'error', error: errorMessage });
        }
      },

      async ensureEducation() {
        if (s.user()?.educations && s.user()!.educations!.length > 0) return;

        try {
          patchState(s, { status: 'loading' });
          if (!s.user()) {
            await this.ensureMe();
          }
          const res = await authService.getUserEducations();
          const currentUser = s.user();

          if (res.educations && currentUser) {
            patchState(s, {
              user: {
                ...currentUser,
                educations: res.educations,
              },
              status: 'loaded',
            });
          } else if (!currentUser) {
            patchState(s, {
              status: 'error',
              error: 'No se encontró una sesión de usuario activa',
            });
          }
        } catch (e: Error | unknown) {
          const errorMessage = e instanceof Error ? e.message : 'Error cargando educaciones';
          patchState(s, { status: 'error', error: errorMessage });
        }
      },

      async ensureEmployments() {
        if (s.user()?.employments && s.user()!.employments!.length > 0) return;

        try {
          patchState(s, { status: 'loading' });
          if (!s.user()) {
            await this.ensureMe();
          }
          const res = await authService.getUserEmployments();
          const currentUser = s.user();
          if (res.employments && currentUser) {
            patchState(s, {
              user: { ...currentUser, employments: res.employments },
              status: 'loaded',
            });
          } else if (!currentUser) {
            patchState(s, {
              status: 'error',
              error: 'No se encontró una sesión de usuario activa',
            });
          }
        } catch (e: Error | unknown) {
          const errorMessage = e instanceof Error ? e.message : 'Error cargando Trabajos';
          patchState(s, { status: 'error', error: errorMessage });
        }
      },

      async getVerifiedIds() {
        try {
          if (s.status() === 'loaded') return;
          if (s.status() === 'loading' && loadMePromise) return loadMePromise;
          const response: ValidateResponse = await authService.validateSession();
          const ids: IUserVerifiedIds = {
            email: false,
            phoneNumber: false,
          };
          if (['AUTH-101', 'AUTH-211', 'AUTH-215'].includes(response.code!)) {
            ids.email = true;
          }
          patchState(s, { verifiedIds: ids, status: 'loaded' });
        } catch (e: Error | unknown) {
          const errorMessage =
            e instanceof Error ? e.message : 'Error cargando identificadores verificados';
          patchState(s, {
            status: 'error',
            error: errorMessage,
          });
        }
      },
    }),
  ),

  withHooks((s) => ({
    onInit() {
      const t = localStorage.getItem('auth_token');
      const token = t && t !== 'undefined' && t !== 'null' ? t : null;
      // const rT = localStorage.getItem('auth_token');
      // const refreshToken = rT && rT !== 'undefined' && rT !== 'null' ? rT : null;

      patchState(s, { token, lastToken: token, status: 'idle' });
    },
  })),
);
